import { Controller } from "@hotwired/stimulus"

/**
 * Input State Controller
 * 
 * Controls the visibility of input action icons based on input field state.
 * 
 * Usage:
 *   <div class='app-input-container' data-controller="input-state">
 *     <input type='text' class='app-input' data-input-state-target="input" />
 *     <img src='input_clear.svg' class='input-action-icon' data-input-state-target="clearIcon" data-action="click->input-state#clear" />
 *     <img src='input_danger.svg' class='input-action-icon' data-input-state-target="dangerIcon" />
 *     <img src='input_ok.svg' class='input-action-icon' data-input-state-target="okIcon" />
 *   </div>
 * 
 * Targets:
 *   - input: The input field to watch
 *   - clearIcon: Icon shown when input has text (clicking clears the input)
 *   - dangerIcon: Icon for danger/error state (for future use)
 *   - okIcon: Icon for success/valid state (for future use)
 */
export default class extends Controller {
  static targets = ["input", "clearIcon", "dangerIcon", "okIcon"]
  static values = { alwaysShowClear: Boolean }

  connect() {
    this.updateIcons()
  }

  inputTargetConnected() {
    this.updateIcons()
  }

  // Called on input event
  onInput() {
    this.updateIcons()
  }

  // Clear the input field
  clear() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.focus()
      this.updateIcons()
      // Dispatch input event so other listeners are notified
      this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }

  updateIcons() {
    const hasText = this.hasInputTarget && this.inputTarget.value.trim().length > 0

    // Show clear icon only when there is text, unless the input opts into an always-visible clear affordance.
    if (this.hasClearIconTarget) {
      this.clearIconTarget.classList.toggle("d-none", !this.alwaysShowClearValue && !hasText)
    }

    // Hide danger and ok icons by default (for future use)
    if (this.hasDangerIconTarget) {
      this.dangerIconTarget.classList.add("d-none")
    }

    if (this.hasOkIconTarget) {
      this.okIconTarget.classList.add("d-none")
    }

    this.updateActionIconState()
  }

  // Future methods for showing danger/ok states
  showDanger() {
    if (this.hasDangerIconTarget) {
      this.dangerIconTarget.classList.remove("d-none")
    }
    this.updateActionIconState()
  }

  hideDanger() {
    if (this.hasDangerIconTarget) {
      this.dangerIconTarget.classList.add("d-none")
    }
    this.updateActionIconState()
  }

  showOk() {
    if (this.hasOkIconTarget) {
      this.okIconTarget.classList.remove("d-none")
    }
    this.updateActionIconState()
  }

  hideOk() {
    if (this.hasOkIconTarget) {
      this.okIconTarget.classList.add("d-none")
    }
    this.updateActionIconState()
  }

  updateActionIconState() {
    const hasVisibleActionIcon = Array.from(this.element.querySelectorAll(".input-action-icon"))
      .some((icon) => !icon.classList.contains("d-none"))

    this.element.classList.toggle("with-visible-action-icon", hasVisibleActionIcon)
  }
}
